import React from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type {
  ByLawsResponse,
  ByLawsSection,
  ByLawsContent,
  ByLawsListItem,
} from '../../../types/chapters';
import {
  LIST_CONTENT_TYPE_ID,
  PARAGRAPH_CONTENT_TYPE_ID,
  createNewSection,
  createNewParagraphContent,
  createNewListContent,
  createNewListItem,
  normalizeOrders,
} from './bylawsEditorConstants';

// --- ListItemsEditor ---
interface ListItemsEditorProps {
  items: ByLawsListItem[];
  onChange: (items: ByLawsListItem[]) => void;
}

function ListItemsEditor({ items, onChange }: ListItemsEditorProps) {
  const sorted = [...items].sort((a, b) => a.order - b.order);

  const updateItem = (index: number, updates: Partial<ByLawsListItem>) => {
    const next = sorted.map((item, i) =>
      i === index ? { ...item, ...updates } : item
    );
    onChange(normalizeOrders(next));
  };

  const removeItem = (index: number) => {
    const next = sorted.filter((_, i) => i !== index);
    onChange(normalizeOrders(next));
  };

  const moveItem = (from: number, direction: -1 | 1) => {
    const to = from + direction;
    if (to < 0 || to >= sorted.length) return;
    const next = [...sorted];
    [next[from], next[to]] = [next[to], next[from]];
    onChange(normalizeOrders(next));
  };

  const addItem = () => {
    onChange(normalizeOrders([...sorted, createNewListItem(sorted.length)]));
  };

  return (
    <Box sx={{ mt: 1 }}>
      {sorted.map((item, index) => (
        <Box
          key={item.id || `li-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5,
            mb: 1,
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="List item"
            value={item.content}
            onChange={(e) => updateItem(index, { content: e.target.value })}
            sx={{ flex: 1 }}
          />
          <IconButton
            size="small"
            onClick={() => moveItem(index, -1)}
            disabled={index === 0}
            aria-label="Move up"
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => moveItem(index, 1)}
            disabled={index === sorted.length - 1}
            aria-label="Move down"
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => removeItem(index)}
            aria-label="Remove item"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addItem}
        sx={{ mt: 0.5 }}
      >
        Add list item
      </Button>
    </Box>
  );
}

// --- ContentBlockEditor ---
interface ContentBlockEditorProps {
  content: ByLawsContent;
  contentIndex: number;
  onChange: (content: ByLawsContent) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

function ContentBlockEditor({
  content,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: ContentBlockEditorProps) {
  const isList = content.content_type_id === LIST_CONTENT_TYPE_ID;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {isList ? 'List' : 'Paragraph'}
        </Typography>
        <IconButton
          size="small"
          onClick={onMoveUp}
          disabled={!canMoveUp}
          aria-label="Move up"
        >
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onMoveDown}
          disabled={!canMoveDown}
          aria-label="Move down"
        >
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={onRemove}
          aria-label="Remove block"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      {isList ? (
        <ListItemsEditor
          items={content.list || []}
          onChange={(list) => onChange({ ...content, list })}
        />
      ) : (
        <>
          <TextField
            size="small"
            fullWidth
            label="Title (optional)"
            value={content.title || ''}
            onChange={(e) => onChange({ ...content, title: e.target.value })}
            sx={{ mb: 1 }}
          />
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Paragraph"
            value={content.paragraph || ''}
            onChange={(e) =>
              onChange({ ...content, paragraph: e.target.value })
            }
          />
        </>
      )}
    </Paper>
  );
}

// --- SectionEditor ---
interface SectionEditorProps {
  section: ByLawsSection;
  sectionIndex: number;
  onChange: (section: ByLawsSection) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onAddParagraph: () => void;
  onAddList: () => void;
}

function SectionEditor({
  section,
  sectionIndex,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onAddParagraph,
  onAddList,
}: SectionEditorProps) {
  const content = [...(section.content || [])].sort(
    (a, b) => a.order - b.order
  );

  const updateContentAtIndex = (index: number, nextContent: ByLawsContent) => {
    const next = content.map((c, i) => (i === index ? nextContent : c));
    onChange({ ...section, content: normalizeOrders(next) });
  };

  const removeContent = (index: number) => {
    const next = content.filter((_, i) => i !== index);
    onChange({ ...section, content: normalizeOrders(next) });
  };

  const moveContent = (index: number, direction: -1 | 1) => {
    const to = index + direction;
    if (to < 0 || to >= content.length) return;
    const next = [...content];
    [next[index], next[to]] = [next[to], next[index]];
    onChange({ ...section, content: normalizeOrders(next) });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label="Section title"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          sx={{ minWidth: 240 }}
        />
        <Typography variant="body2" color="text.secondary">
          #{sectionIndex + 1}
        </Typography>
        <IconButton
          size="small"
          onClick={onMoveUp}
          disabled={!canMoveUp}
          aria-label="Move section up"
        >
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onMoveDown}
          disabled={!canMoveDown}
          aria-label="Move section down"
        >
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={onRemove}
          aria-label="Remove section"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider sx={{ my: 2 }} />
      {content.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add content below.
        </Typography>
      )}
      {content.map((c, index) => (
        <ContentBlockEditor
          key={c.id || `content-${index}`}
          content={c}
          contentIndex={index}
          onChange={(next) => updateContentAtIndex(index, next)}
          onRemove={() => removeContent(index)}
          onMoveUp={() => moveContent(index, -1)}
          onMoveDown={() => moveContent(index, 1)}
          canMoveUp={index > 0}
          canMoveDown={index < content.length - 1}
        />
      ))}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddParagraph}
        >
          Add paragraph
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddList}
        >
          Add list
        </Button>
      </Box>
    </Paper>
  );
}

// --- BylawsEditor ---
interface BylawsEditorProps {
  payload: ByLawsResponse;
  onChange: (payload: ByLawsResponse) => void;
}

export function BylawsEditor({ payload, onChange }: BylawsEditorProps) {
  const sections = [...(payload.sections || [])].sort(
    (a, b) => a.order - b.order
  );

  const updateSectionAtIndex = (index: number, nextSection: ByLawsSection) => {
    const next = sections.map((s, i) => (i === index ? nextSection : s));
    onChange({ ...payload, sections: normalizeOrders(next) });
  };

  const removeSection = (index: number) => {
    const next = sections.filter((_, i) => i !== index);
    onChange({ ...payload, sections: normalizeOrders(next) });
  };

  const moveSection = (index: number, direction: -1 | 1) => {
    const to = index + direction;
    if (to < 0 || to >= sections.length) return;
    const next = [...sections];
    [next[index], next[to]] = [next[to], next[index]];
    onChange({ ...payload, sections: normalizeOrders(next) });
  };

  const addSection = () => {
    onChange({
      ...payload,
      sections: normalizeOrders([
        ...sections,
        createNewSection(sections.length),
      ]),
    });
  };

  const addParagraphToSection = (sectionIndex: number) => {
    const section = sections[sectionIndex];
    const content = [...(section.content || [])];
    const newContent = createNewParagraphContent(
      content.length,
      PARAGRAPH_CONTENT_TYPE_ID
    );
    const nextContent = normalizeOrders([...content, newContent]);
    updateSectionAtIndex(sectionIndex, { ...section, content: nextContent });
  };

  const addListToSection = (sectionIndex: number) => {
    const section = sections[sectionIndex];
    const content = [...(section.content || [])];
    const newContent = createNewListContent(
      content.length,
      LIST_CONTENT_TYPE_ID
    );
    const nextContent = normalizeOrders([...content, newContent]);
    updateSectionAtIndex(sectionIndex, { ...section, content: nextContent });
  };

  return (
    <Box>
      {sections.map((section, index) => (
        <SectionEditor
          key={section.id || `sec-${index}`}
          section={section}
          sectionIndex={index}
          onChange={(next) => updateSectionAtIndex(index, next)}
          onRemove={() => removeSection(index)}
          onMoveUp={() => moveSection(index, -1)}
          onMoveDown={() => moveSection(index, 1)}
          canMoveUp={index > 0}
          canMoveDown={index < sections.length - 1}
          onAddParagraph={() => addParagraphToSection(index)}
          onAddList={() => addListToSection(index)}
        />
      ))}
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addSection}
        sx={{ mt: 2 }}
      >
        Add section
      </Button>
    </Box>
  );
}

export default BylawsEditor;
